import React from 'react';

/**
 * SocialAuthButton Component
 *
 * Renders OAuth provider buttons (Google, Facebook, Apple, GitHub) with proper branding
 *
 * @param {Object} props
 * @param {'google'|'facebook'|'apple'|'github'} props.provider - OAuth provider name
 * @param {Function} props.onClick - Click handler for OAuth flow
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {boolean} props.loading - Loading state
 * @param {'signin'|'signup'} props.mode - Sign in or sign up mode (affects button text)
 */
const SocialAuthButton = ({
  provider,
  onClick,
  disabled = false,
  loading = false,
  mode = 'signin',
}) => {
  const providerConfig = {
    google: {
      name: 'Google',
      bgColor: 'bg-white hover:bg-gray-50',
      textColor: 'text-gray-900',
      borderColor: 'border-gray-300',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      ),
    },
    facebook: {
      name: 'Facebook',
      bgColor: 'bg-[#1877F2] hover:bg-[#1565C0]',
      textColor: 'text-white',
      borderColor: 'border-[#1877F2]',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
    },
    apple: {
      name: 'Apple',
      bgColor: 'bg-black hover:bg-gray-900',
      textColor: 'text-white',
      borderColor: 'border-black',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
      ),
    },
    github: {
      name: 'GitHub',
      bgColor: 'bg-[#24292e] hover:bg-[#1a1e22]',
      textColor: 'text-white',
      borderColor: 'border-[#24292e]',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
        </svg>
      ),
    },
  };

  const config = providerConfig[provider];
  if (!config) {
    console.error(`Unknown provider: ${provider}`);
    return null;
  }

  const buttonText =
    mode === 'signin' ? `Sign in with ${config.name}` : `Sign up with ${config.name}`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl
        border ${config.borderColor} ${config.bgColor} ${config.textColor}
        font-medium transition-all
        disabled:opacity-50 disabled:cursor-not-allowed
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400
      `}
    >
      {loading ? (
        <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      ) : (
        <span className="shrink-0">{config.icon}</span>
      )}
      <span className="min-w-0 text-center leading-snug whitespace-normal break-words">
        {buttonText}
      </span>
    </button>
  );
};

/**
 * SocialAuthButtons Component
 *
 * Renders a group of social OAuth buttons with optional divider
 *
 * @param {Object} props
 * @param {Array<'google'|'facebook'|'apple'|'github'>} props.providers - List of providers to show
 * @param {Function} props.onProviderClick - Click handler receiving provider name
 * @param {boolean} props.disabled - Whether all buttons are disabled
 * @param {'signin'|'signup'} props.mode - Sign in or sign up mode
 * @param {boolean} props.showDivider - Show "OR" divider below buttons
 * @param {'vertical'|'horizontal'} props.layout - Layout direction for buttons
 */
export const SocialAuthButtons = ({
  providers = ['google'],
  onProviderClick,
  disabled = false,
  mode = 'signin',
  showDivider = true,
  layout = 'vertical',
}) => {
  const [loadingProvider, setLoadingProvider] = React.useState(null);

  const handleClick = async (provider) => {
    setLoadingProvider(provider);
    try {
      await onProviderClick?.(provider);
    } finally {
      setLoadingProvider(null);
    }
  };

  const providerConfig = {
    google: {
      name: 'Google',
      bgColor: 'bg-white hover:bg-gray-50',
      textColor: 'text-gray-900',
      borderColor: 'border-gray-300',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      ),
    },
    facebook: {
      name: 'Facebook',
      bgColor: 'bg-[#1877F2] hover:bg-[#1565C0]',
      textColor: 'text-white',
      borderColor: 'border-[#1877F2]',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
    },
  };

  return (
    <div>
      <div
        className={layout === 'horizontal' ? 'flex flex-col sm:flex-row gap-3 w-full' : 'space-y-3'}
      >
        {providers.map((provider) => {
          const config = providerConfig[provider];
          if (!config) return null;

          return (
            <button
              key={provider}
              type="button"
              onClick={() => handleClick(provider)}
              disabled={disabled || loadingProvider !== null}
              className={`
                ${layout === 'horizontal' ? 'w-full sm:flex-1 min-w-0' : 'w-full'}
                flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl
                border ${config.borderColor} ${config.bgColor} ${config.textColor}
                font-medium text-sm transition-all
                disabled:opacity-50 disabled:cursor-not-allowed
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400
              `}
            >
              {loadingProvider === provider ? (
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="shrink-0">{config.icon}</span>
              )}
              <span className="min-w-0 text-center leading-snug whitespace-normal break-words">
                {mode === 'signin' ? `Sign in with ${config.name}` : `Sign up with ${config.name}`}
              </span>
            </button>
          );
        })}
      </div>

      {showDivider && (
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-[#0f1115] text-[#d8d2c5]">OR</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialAuthButton;
